import React, { useState } from 'react';
import {
  formatCentsToDollars,
  UNATTRIBUTED_COLOR,
} from '../../utils/attributionUtils';

export interface AllocationSegment {
  truck_id: string;
  truck_display_name: string;
  amount_cents: number;
  color: string;
}

interface AllocationChartProps {
  allocations: AllocationSegment[];
  unattributedCents: number;
  totalCents: number;
  height?: number;
}

const AllocationChart: React.FC<AllocationChartProps> = ({
  allocations,
  unattributedCents,
  totalCents,
  height = 12,
}) => {
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);

  // Include unattributed segment if there's any unattributed amount
  const segments = [
    ...allocations.filter((a) => a.amount_cents > 0),
    ...(unattributedCents > 0
      ? [
          {
            truck_id: 'unattributed',
            truck_display_name: 'Business',
            amount_cents: unattributedCents,
            color: UNATTRIBUTED_COLOR,
          },
        ]
      : []),
  ];

  // Empty state
  if (segments.length === 0 || totalCents === 0) {
    return (
      <div
        className="flex w-full items-center justify-center overflow-hidden rounded-full bg-[#F3F4F6]"
        style={{ height }}
      >
        <div className="h-full w-full bg-gradient-to-r from-[#F3F4F6] to-[#E5E7EB]" />
      </div>
    );
  }

  // Calculate percentages for simple div-based bar (more reliable than Recharts for this use case)
  const segmentWidths = segments.map((segment) => ({
    ...segment,
    width: totalCents > 0 ? (segment.amount_cents / totalCents) * 100 : 0,
  }));

  return (
    <div
      className="flex w-full overflow-hidden rounded-full bg-[#F3F4F6]"
      style={{ height }}
    >
      {segmentWidths.map((segment, index) => (
        <div
          key={segment.truck_id}
          className="relative h-full transition-all duration-200"
          style={{
            width: `${segment.width}%`,
            backgroundColor: segment.color,
            marginLeft: index > 0 ? '1px' : 0,
          }}
          onMouseEnter={() => setHoveredSegment(segment.truck_id)}
          onMouseLeave={() => setHoveredSegment(null)}
        >
          {/* Hover tooltip */}
          {hoveredSegment === segment.truck_id && (
            <div className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 whitespace-nowrap rounded-[6px] border border-[#E5E7EB] bg-white px-3 py-2 shadow-lg">
              <div className="flex items-center gap-2">
                <div
                  className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: segment.color }}
                />
                <div>
                  <p className="text-[12px] font-medium text-primary">
                    {segment.truck_display_name}
                  </p>
                  <p className="text-[11px] tabular-nums text-[#6B7280]">
                    {formatCentsToDollars(segment.amount_cents)} (
                    {segment.width.toFixed(1)}%)
                  </p>
                </div>
              </div>
              {/* Arrow */}
              <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-white" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AllocationChart;
